import { getOperationOutcomeMessage, isVersionConflictOutcome } from '@bariendo/lib/error';
import { PatchOperation } from '@medplum/core';
import { Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useState } from 'react';
import { alert } from '@/services/alert';

/**
 * Shared executor for every clinical-task write (complete, reopen, edit,
 * delete): prepends a `test /meta/versionId` guard plus a lastModified
 * update to the caller's ops, so a concurrent edit fails loudly instead of
 * clobbering.
 *
 * `onChange` is AWAITED before `isUpdating` clears: the row only leaves the
 * list once the refresh lands, so releasing the buttons earlier reopens a
 * window where a double-click hits the stale row and produces a spurious
 * version-conflict alert. On a conflict, `onChange` still runs — the row the
 * user acted on is stale and must be replaced.
 */
export function useGuardedTaskPatch(onChange?: () => void | Promise<void>) {
  const medplum = useMedplum();
  const [isUpdating, setIsUpdating] = useState(false);

  const patchTask = useCallback(
    async (task: Task, ops: PatchOperation[]): Promise<Task | undefined> => {
      if (!task.id) {
        alert.error('Cannot update task: missing task id');
        return undefined;
      }
      if (!task.meta?.versionId) {
        // A test op whose `value` is undefined serializes without the key and
        // fails against any real version — surfacing the misleading conflict
        // message. Refuse up front instead.
        alert.error('Cannot update task: missing version. Refresh and try again.');
        return undefined;
      }

      setIsUpdating(true);
      try {
        const now = new Date().toISOString();
        const lastModifiedOp: PatchOperation =
          task.lastModified !== undefined
            ? { op: 'replace', path: '/lastModified', value: now }
            : { op: 'add', path: '/lastModified', value: now };
        const patch: PatchOperation[] = [
          { op: 'test', path: '/meta/versionId', value: task.meta.versionId },
          lastModifiedOp,
          ...ops,
        ];

        const updated = await medplum.patchResource('Task', task.id, patch);
        await onChange?.();
        return updated;
      } catch (error) {
        if (isVersionConflictOutcome(error)) {
          alert.error('Failed to update task: it was changed in the meantime. Refresh and try again.');
          await onChange?.();
          return undefined;
        }
        alert.error('Failed to update task: ' + getOperationOutcomeMessage(error));
        return undefined;
      } finally {
        setIsUpdating(false);
      }
    },
    [medplum, onChange]
  );

  return { patchTask, isUpdating };
}
